/**
 * Publishes `useActiveTeam`: the caller's teams, and which one is selected.
 * The model — why `team` is never null, and what the alias must not do — is in
 * `./activeTeam`.
 */
import * as React from "react";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useSession } from "@authe/ui-core/session";
import { useMyTeams } from "@/features/team/useMyTeams";
import { useStorageIdentity } from "./storageIdentity";
import { resolveActiveTeam } from "./activeTeam.resolve";
import { ActiveTeamContext, type ActiveTeam } from "./activeTeam";

/** Storage key for the selected team id, inside the identity-scoped keyspace. */
const ACTIVE_TEAM_KEY = "active-team-id";

export function ActiveTeamProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { session } = useSession();
  const identity = useStorageIdentity();
  const { teams, isLoading, error, refetch, invalidate } = useMyTeams();

  const storage = React.useMemo(
    () => createScopedStorage(identity),
    // The identity object is rebuilt each render; its FIELDS are the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId],
  );

  const [selectedTeamId, setSelectedTeamId] = React.useState<string | null>(() =>
    storage.get<string | null>(ACTIVE_TEAM_KEY, null),
  );

  // A tenant switch re-keys the storage, so re-seed from the new keyspace
  // rather than carrying the previous tenant's team id into it.
  const seededFrom = React.useRef(storage);
  if (seededFrom.current !== storage) {
    seededFrom.current = storage;
    setSelectedTeamId(storage.get<string | null>(ACTIVE_TEAM_KEY, null));
  }

  const setActiveTeamId = React.useCallback(
    (teamId: string) => {
      setSelectedTeamId(teamId);
      storage.set(ACTIVE_TEAM_KEY, teamId);
    },
    [storage],
  );

  const resolved = React.useMemo(
    () =>
      resolveActiveTeam({
        teams,
        selectedTeamId,
        tenant: {
          orgId: session?.tenant.orgId ?? null,
          tenantId: session?.tenant.tenantId ?? null,
        },
      }),
    [teams, selectedTeamId, session],
  );

  const value = React.useMemo<ActiveTeam>(
    () => ({
      teams,
      team: resolved.team,
      selectedTeamId: resolved.selectedTeamId,
      setActiveTeamId,
      isLoading,
      // Carried, not swallowed: the alias is a reasonable thing to CALL with
      // and a terrible thing to present as the caller's team.
      failed: error != null,
      error,
      isFallback: resolved.isFallback,
      refetch,
      invalidate,
    }),
    [teams, resolved, setActiveTeamId, isLoading, error, refetch, invalidate],
  );

  return <ActiveTeamContext.Provider value={value}>{children}</ActiveTeamContext.Provider>;
}
